/**
 * The card: the unit every page is built from.
 *
 * `tone` tints only the top edge, and exists so a card carrying a problem reads
 * as one before its contents are parsed. Untoned is the normal case — the whole
 * point of the design is that a healthy fleet is visually quiet, so a coloured
 * card should be rare enough to draw the eye on its own.
 */

import type { ReactNode } from 'react';

import type { Tone } from '../Badge';

export function Card({
  children,
  tone,
  className,
  flush = false,
  role,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
  /** Remove body padding — for a card whose whole body is a table. */
  flush?: boolean;
  /** A card that announces itself — e.g. an auth gate whose state changed
   *  mid-session needs role="alert" for it to be read out at all. */
  role?: string;
  'aria-label'?: string;
  /** Points at a heading rendered inside the card. Preferred over aria-label
   *  when the card already displays its own title — it keeps one source of
   *  truth for the name instead of two that can drift apart. */
  'aria-labelledby'?: string;
}) {
  return (
    <section
      className={[
        'card',
        tone ? `card--${tone}` : '',
        flush ? 'card--flush' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      role={role}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  subtitle,
  actions,
  count,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  count?: ReactNode;
}) {
  return (
    <header className="card__header">
      <div className="card__header-text">
        <h2 className="card__title">
          {title}
          {count != null ? <span className="card__count">{count}</span> : null}
        </h2>
        {subtitle ? <p className="card__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="card__actions">{actions}</div> : null}
    </header>
  );
}

export function CardBody({ children, flush = false }: { children: ReactNode; flush?: boolean }) {
  return <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>;
}

/**
 * A definition list for a record's fields. Grid-aligned so the values form a
 * column the eye can run down, rather than ragged text after ragged labels.
 */
export function KeyValue({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={['kv', className ?? ''].filter(Boolean).join(' ')}>{children}</dl>;
}

export function KeyValueRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
